(() => {
  const THEME_OPTIONS = [
    { value: 'raceTrack', label: 'Race Track' },
    { value: 'offRoadTrail', label: 'Off-Road Trail' },
    { value: 'longDistanceCruise', label: 'Long-Distance Cruise' },
    { value: 'geekDiagnostic', label: 'Geek Diagnostic' },
    { value: 'cyberNightRide', label: 'Cyber Night Ride' },
    { value: 'minimalLight', label: 'Minimal Light' },
    { value: 'minimalDark', label: 'Minimal Dark' }
  ];

  const VALID_THEMES = new Set(THEME_OPTIONS.map((option) => option.value));
  const THEME_LABELS = THEME_OPTIONS.reduce((map, option) => {
    map[option.value] = option.label;
    return map;
  }, {});

  const ALIAS_TO_THEME = {
    racetrack: 'raceTrack',
    race: 'raceTrack',
    offroadtrail: 'offRoadTrail',
    offroad: 'offRoadTrail',
    longdistancecruise: 'longDistanceCruise',
    oceantech: 'longDistanceCruise',
    ocean: 'longDistanceCruise',
    geekdiagnostic: 'geekDiagnostic',
    graphite: 'geekDiagnostic',
    cybernightride: 'cyberNightRide',
    cyber: 'cyberNightRide',
    minimallight: 'minimalLight',
    light: 'minimalLight',
    minimaldark: 'minimalDark',
    dark: 'minimalDark',
    industrialtech: 'industrialTech',
    industrial: 'industrialTech',
    steel: 'industrialTech'
  };

  const REVEAL_SELECTORS = [
    '.shopify-section',
    '.product-card',
    '.collection-card',
    '.blog-post',
    '.gallery--item',
    '.bvg-card',
    '.bcp-card',
    '.bcp-support-card',
    '.bep-card',
    '.bep-support-card',
    '.bhf-style-card',
    '.bhf-product-card',
    '.bhf-news-card'
  ];

  const CARD_MOTION_SELECTORS = [
    '.product-card',
    '.collection-card',
    '.blog-post',
    '.gallery--item',
    '.bvg-card',
    '.bcp-card',
    '.bcp-support-card',
    '.bep-card',
    '.bep-support-card',
    '.bhf-style-card',
    '.bhf-product-card',
    '.bhf-news-card'
  ];

  const THEME_PICKER_ITEMS = new Set();
  let revealObserver = null;
  const REVEAL_VISIBLE_FALLBACK_MS = 850;

  function normalizeTheme(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return 'cyberNightRide';
    if (VALID_THEMES.has(raw)) return raw;

    const compact = raw.trim().toLowerCase().replace(/[\s_-]+/g, '');
    const mapped = ALIAS_TO_THEME[compact];
    if (mapped && VALID_THEMES.has(mapped)) return mapped;

    return 'cyberNightRide';
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isCoarsePointer() {
    return window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  function shouldUseLightMotionMode() {
    return prefersReducedMotion() || isCoarsePointer();
  }

  function saveTheme(theme) {
    try {
      window.localStorage.setItem('appTheme', theme);
      window.localStorage.setItem('begode_theme', theme);
    } catch (error) {
      // Ignore storage failures and continue with runtime theme.
    }
  }

  function updateThemeLabels(theme) {
    const label = THEME_LABELS[theme] || THEME_LABELS.cyberNightRide;
    document.querySelectorAll('[data-begode-theme-name]').forEach((node) => {
      node.textContent = label;
    });
  }

  function syncThemePicker(select) {
    const picker = select.parentElement?.querySelector('.begode-theme-picker');
    if (!picker) return;

    const triggerLabel = picker.querySelector('.begode-theme-picker__label');
    const activeTheme = normalizeTheme(select.value);
    const selectedOption = select.options[select.selectedIndex];
    const selectedLabel = selectedOption?.textContent?.trim() || THEME_LABELS[activeTheme] || THEME_LABELS.cyberNightRide;

    if (triggerLabel) triggerLabel.textContent = selectedLabel;

    picker.querySelectorAll('.begode-theme-picker__option').forEach((optionBtn) => {
      const isActive = normalizeTheme(optionBtn.dataset.value || '') === activeTheme;
      optionBtn.classList.toggle('is-active', isActive);
      optionBtn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  function updateThemePickers() {
    document.querySelectorAll('select[data-begode-theme-select]').forEach((select) => {
      syncThemePicker(select);
    });
  }

  function updateThemeSelects(theme) {
    document.querySelectorAll('select[data-begode-theme-select]').forEach((select) => {
      if (select.value !== theme) select.value = theme;
    });
  }

  function setTheme(theme, options = {}) {
    const normalized = normalizeTheme(theme);
    const shouldPersist = options.persist !== false;
    const shouldBroadcast = options.broadcast !== false;

    document.documentElement.setAttribute('data-app-theme', normalized);
    document.documentElement.style.colorScheme = normalized === 'minimalLight' ? 'light' : 'dark';

    updateThemeSelects(normalized);
    updateThemePickers();
    updateThemeLabels(normalized);

    if (shouldPersist) saveTheme(normalized);

    if (shouldBroadcast) {
      document.dispatchEvent(
        new CustomEvent('begode:theme-change', {
          detail: { theme: normalized, label: THEME_LABELS[normalized] || '' }
        })
      );
    }
  }

  function closeAllThemeMenus(exceptPicker = null) {
    THEME_PICKER_ITEMS.forEach((item) => {
      if (item.picker === exceptPicker) return;
      item.picker.classList.remove('is-open');
      item.trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function buildThemePicker(select) {
    const field = select.closest('.begode-global-theme__field');
    if (!field) return;
    const existingPicker = field.querySelector('.begode-theme-picker');

    if (existingPicker instanceof HTMLElement) {
      existingPicker.remove();
    }

    field.classList.remove('begode-theme-picker-ready');
    select.classList.remove('begode-theme-picker__native');
    select.removeAttribute('tabindex');
    select.dataset.begodeThemeEnhanced = 'native';
  }

  function bindSelect(select) {
    if (select.dataset.begodeThemeBound === 'true') return;
    select.dataset.begodeThemeBound = 'true';

    buildThemePicker(select);

    select.addEventListener('change', () => {
      syncThemePicker(select);
      setTheme(select.value, { persist: true, broadcast: true });
    });
  }

  function bindAllSelects(root = document) {
    root.querySelectorAll('select[data-begode-theme-select]').forEach((select) => bindSelect(select));
  }

  function submitLocalizationForm(form) {
    if (!(form instanceof HTMLFormElement)) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    form.submit();
  }

  function persistLanguagePreference(rawCode) {
    if (typeof rawCode !== 'string' || !rawCode.trim()) return;
    const code = rawCode.trim().toLowerCase();
    const appLang = code;
    try {
      window.localStorage.setItem('appLang', appLang);
      window.localStorage.setItem('begode_language_preference', code);
    } catch (error) {
      // Ignore storage failures.
    }
    document.documentElement.setAttribute('lang', appLang);
  }

  function bindLocalization(root = document) {
    root.querySelectorAll('select[name="locale_code"], select[name="country_code"]').forEach((select) => {
      if (select.dataset.begodeLocaleBound === 'true') return;
      select.dataset.begodeLocaleBound = 'true';
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (select.name === 'locale_code') persistLanguagePreference(select.value || '');
        if (form) submitLocalizationForm(form);
      });
    });

    root.querySelectorAll('localization-form input[name="locale_code"], localization-form input[name="country_code"]').forEach((input) => {
      if (input.dataset.begodeLocaleBound === 'true') return;
      input.dataset.begodeLocaleBound = 'true';
      input.addEventListener('change', () => {
        if (input.name === 'locale_code') persistLanguagePreference(input.value || '');
      });
    });
  }

  function collectTargets(root, selectors) {
    const targets = [];
    if (root instanceof HTMLElement) {
      selectors.forEach((selector) => {
        if (root.matches(selector)) targets.push(root);
      });
      selectors.forEach((selector) => {
        root.querySelectorAll(selector).forEach((node) => targets.push(node));
      });
    } else {
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => targets.push(node));
      });
    }
    return Array.from(new Set(targets)).filter((node) => {
      if (!(node instanceof HTMLElement)) return false;
      return node.closest('#main-content') !== null;
    });
  }

  function forceRevealVisible(root = document) {
    const targets = collectTargets(root, REVEAL_SELECTORS);
    targets.forEach((target) => {
      if (!(target instanceof HTMLElement)) return;
      target.classList.add('begode-reveal', 'is-visible');
    });
  }

  function ensureRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return;
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.12
      }
    );
  }

  function isNearViewport(target) {
    if (!(target instanceof HTMLElement)) return false;
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (viewportHeight <= 0) return false;
    return rect.top < viewportHeight * 1.12 && rect.bottom > -viewportHeight * 0.12;
  }

  function initRevealEffects(root = document) {
    if (shouldUseLightMotionMode()) {
      forceRevealVisible(root);
      return;
    }
    ensureRevealObserver();
    document.documentElement.classList.add('begode-reveal-active');

    const targets = collectTargets(root, REVEAL_SELECTORS);
    targets.forEach((target, index) => {
      if (!(target instanceof HTMLElement)) return;
      if (target.dataset.begodeRevealBound === 'true') return;
      target.dataset.begodeRevealBound = 'true';
      target.classList.add('begode-reveal');
      target.style.setProperty('--reveal-delay', `${(index % 6) * 55}ms`);

      if (!revealObserver) {
        target.classList.add('is-visible');
        return;
      }

      if (isNearViewport(target)) {
        target.classList.add('is-visible');
        return;
      }

      revealObserver.observe(target);
      window.setTimeout(() => {
        if (!target.classList.contains('is-visible')) {
          target.classList.add('is-visible');
        }
      }, REVEAL_VISIBLE_FALLBACK_MS + (index % 6) * 55);
    });
  }

  function bindMotionCard(card) {
    if (shouldUseLightMotionMode()) return;
    if (card.dataset.begodeMotionBound === 'true') return;

    card.dataset.begodeMotionBound = 'true';
    card.classList.add('begode-motion-card');

    card.addEventListener('pointerenter', () => {
      card.classList.add('is-pointer');
    });

    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      const pointerX = ((event.clientX - rect.left) / rect.width) * 100;
      const pointerY = ((event.clientY - rect.top) / rect.height) * 100;
      card.style.setProperty('--pointer-x', `${Math.max(0, Math.min(100, pointerX)).toFixed(2)}%`);
      card.style.setProperty('--pointer-y', `${Math.max(0, Math.min(100, pointerY)).toFixed(2)}%`);
    });

    card.addEventListener('pointerleave', () => {
      card.classList.remove('is-pointer');
      card.style.removeProperty('--pointer-x');
      card.style.removeProperty('--pointer-y');
    });
  }

  function initMotionCards(root = document) {
    const cards = collectTargets(root, CARD_MOTION_SELECTORS);
    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      bindMotionCard(card);
    });
  }

  function initGlobalEffects(root = document) {
    initRevealEffects(root);
    initMotionCards(root);
  }

  function galleryStageLooksEmpty(gallery) {
    if (!(gallery instanceof HTMLElement)) return false;
    const slider = gallery.querySelector('.product-image-container .product-images');
    if (!(slider instanceof HTMLElement)) return false;

    const viewport = slider.querySelector('.flickity-viewport');
    const selectedSlide = slider.querySelector('.product-images__slide.is-selected, .product-images__slide.is-initial-selected');
    const selectedMedia = selectedSlide?.querySelector('.product-single__media');

    const viewportRect = viewport?.getBoundingClientRect();
    const mediaRect = selectedMedia?.getBoundingClientRect();

    if (mediaRect && mediaRect.width > 80 && mediaRect.height > 80) return false;
    if (viewportRect && viewportRect.width > 80 && viewportRect.height > 80) return false;

    const anyVisibleMedia = Array.from(slider.querySelectorAll('.product-single__media')).some((node) => {
      if (!(node instanceof HTMLElement)) return false;
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      return rect.width > 80 && rect.height > 80 && style.display !== 'none' && style.visibility !== 'hidden';
    });

    return !anyVisibleMedia;
  }

  function nudgeProductGallery(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    const thumbs = Array.from(gallery.querySelectorAll('.product-thumbnail-container .product-thumbnail'));
    if (!thumbs.length) return;

    const preferred = thumbs.find((thumb) => !thumb.querySelector('.product-thumbnail-icon')) || thumbs[0];
    preferred.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }

  function ensureProductMediaReady(root = document) {
    const scope = root instanceof HTMLElement ? root : document;
    const galleries = scope.querySelectorAll('.thb-product-detail .product-gallery-container');
    if (!galleries.length) return;

    galleries.forEach((gallery) => {
      const run = () => {
        if (galleryStageLooksEmpty(gallery)) nudgeProductGallery(gallery);
      };
      run();
      window.setTimeout(run, 280);
      window.setTimeout(run, 860);
      window.setTimeout(run, 1600);
    });
  }

  function normalizePathname(pathname) {
    const normalized = (pathname || '/').replace(/\/+$/, '');
    return normalized || '/';
  }

  function normalizeLinkPath(rawHref, fallback = '/') {
    try {
      return normalizePathname(new URL(rawHref || fallback, window.location.origin).pathname);
    } catch (error) {
      return normalizePathname(fallback);
    }
  }

  function isAppFallbackMatch(rawHref, currentPath) {
    if (!currentPath.startsWith('/pages/app')) return false;
    return rawHref.includes('/pages/app') || rawHref.includes('/pages/app-download') || rawHref.includes('#begode-app-download');
  }

  function isCurrentNavLinkActive(rawHref, currentPath, currentHash) {
    let isActive = false;

    try {
      const targetUrl = new URL(rawHref || '/', window.location.origin);
      const targetPath = normalizePathname(targetUrl.pathname);
      const targetHash = targetUrl.hash || '';

      if (targetHash) {
        isActive = targetPath === currentPath && targetHash === currentHash;
      } else if (currentHash) {
        isActive = false;
      } else {
        isActive = targetPath === currentPath;
      }
    } catch (error) {
      isActive = false;
    }

    if (!isActive && isAppFallbackMatch(rawHref, currentPath)) {
      isActive = true;
    }

    return isActive;
  }

  function syncHeaderMenuCurrentState() {
    const currentPath = normalizePathname(window.location.pathname || '/');
    const currentHash = window.location.hash || '';

    document.querySelectorAll('.thb-full-menu > li').forEach((item) => {
      const link = item.querySelector(':scope > a[data-begode-nav-link]');
      if (!(link instanceof HTMLAnchorElement)) return;

      const rawHref = link.getAttribute('href') || '';
      const isActive = isCurrentNavLinkActive(rawHref, currentPath, currentHash);

      item.classList.toggle('active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function syncHeaderMobileNavigationCurrentState() {
    const currentPath = normalizePathname(window.location.pathname || '/');
    const currentHash = window.location.hash || '';

    document.querySelectorAll('.header-mobile-navigation .header-mobile-navigation--link').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const rawHref = link.getAttribute('href') || '';
      const isActive = isCurrentNavLinkActive(rawHref, currentPath, currentHash);

      link.classList.toggle('is-active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function syncHeaderSecondaryState() {
    const currentPath = normalizePathname(window.location.pathname || '/');
    const searchDrawerActive = document.getElementById('Search-Drawer')?.classList.contains('active') || false;
    const cartDrawerActive = document.getElementById('Cart-Drawer')?.classList.contains('active') || false;

    document.querySelectorAll('.thb-secondary-area-item[data-begode-secondary-link]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const linkType = link.dataset.begodeSecondaryLink || '';
      let isActive = false;

      if (linkType === 'search') {
        isActive = searchDrawerActive || currentPath === normalizeLinkPath(link.getAttribute('href'), '/search');
      } else if (linkType === 'account') {
        isActive = currentPath === '/account' || currentPath.startsWith('/account/');
      } else if (linkType === 'cart') {
        isActive = cartDrawerActive || currentPath === normalizeLinkPath(link.getAttribute('href'), '/cart');
      }

      link.classList.toggle('is-active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function resolveInitialTheme() {
    const fromHtml = normalizeTheme(document.documentElement.getAttribute('data-app-theme'));
    let fromStorage = fromHtml;

    try {
      fromStorage = normalizeTheme(window.localStorage.getItem('appTheme') || window.localStorage.getItem('begode_theme') || fromHtml);
    } catch (error) {
      fromStorage = fromHtml;
    }

    return fromStorage;
  }

  function init() {
    if (shouldUseLightMotionMode()) {
      document.documentElement.classList.add('begode-reduced-motion');
      document.documentElement.classList.remove('begode-reveal-active');
    } else {
      document.documentElement.classList.remove('begode-reduced-motion');
      document.documentElement.classList.add('begode-reveal-active');
    }

    bindAllSelects(document);
    bindLocalization(document);
    setTheme(resolveInitialTheme(), { persist: false, broadcast: false });
    initGlobalEffects(document);
    ensureProductMediaReady(document);
    syncHeaderMenuCurrentState();
    syncHeaderMobileNavigationCurrentState();
    syncHeaderSecondaryState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    let insidePicker = false;
    THEME_PICKER_ITEMS.forEach((item) => {
      if (item.picker.contains(target)) insidePicker = true;
    });
    if (!insidePicker) closeAllThemeMenus();

    if (target.closest('.thb-quick-search, #cart-drawer-toggle, side-panel-close, .click-capture')) {
      window.setTimeout(syncHeaderSecondaryState, 0);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllThemeMenus();
  });

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    window.setTimeout(syncHeaderSecondaryState, 0);
  });

  window.addEventListener('hashchange', () => {
    window.setTimeout(() => {
      syncHeaderMenuCurrentState();
      syncHeaderMobileNavigationCurrentState();
    }, 0);
  });

  window.addEventListener('popstate', () => {
    window.setTimeout(() => {
      syncHeaderMenuCurrentState();
      syncHeaderMobileNavigationCurrentState();
      syncHeaderSecondaryState();
    }, 0);
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== 'appTheme' && event.key !== 'begode_theme') return;
    if (!event.newValue) return;
    setTheme(event.newValue, { persist: false, broadcast: true });
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    bindAllSelects(event.target);
    bindLocalization(event.target);
    setTheme(resolveInitialTheme(), { persist: false, broadcast: false });
    initGlobalEffects(event.target);
    ensureProductMediaReady(event.target);
    syncHeaderMenuCurrentState();
    syncHeaderMobileNavigationCurrentState();
    syncHeaderSecondaryState();
  });

  document.addEventListener('product:variant-change', () => {
    window.setTimeout(() => ensureProductMediaReady(document), 120);
  });

  window.addEventListener('hashchange', () => {
    syncHeaderMenuCurrentState();
    syncHeaderSecondaryState();
  });
  window.addEventListener('popstate', () => {
    syncHeaderMenuCurrentState();
    syncHeaderSecondaryState();
  });
})();
